import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api, type ImageAnnotations, type ImageMeasurement } from '../api';
import { useI18n } from '../i18n';
import { describe } from '../errors';

/**
 * The image viewer: what sensor software gives a dentist, in the record.
 *
 * Zoom and pan, brightness/contrast/gamma ("window"), invert, rotate and
 * flip; a length tool, an angle tool, and a scale set from a known length or
 * the sensor's pixel size so lengths read in millimetres. Measurements are
 * kept in the IMAGE's own pixel coordinates and saved with the image
 * (`attachment.annotations`), so they are the same on every device and in
 * the offline application. The image bytes are never touched.
 *
 * Geometry: the stage is the dark area; the canvas (image + SVG overlay) sits
 * centred in it and carries one CSS transform — translate, scale, rotate,
 * flip. Pointer positions are mapped back through the inverse of that
 * transform to image pixels, so a line drawn at any zoom lands where the
 * pointer was.
 */
export interface ViewerImage {
  id: string;
  kind: string;
  at: string;
  mimeType?: string;
  isSynthetic?: boolean;
  toothFdi?: string | null;
  zone?: string | null;
}

type Tool = 'pan' | 'window' | 'length' | 'angle' | 'calibrate';
type Pt = [number, number];
type Adjustments = { brightness: number; contrast: number; gamma: number; invert: boolean; rotate: 0 | 90 | 180 | 270; flipH: boolean; flipV: boolean };

const DEFAULT_ADJ: Adjustments = { brightness: 0, contrast: 0, gamma: 1, invert: false, rotate: 0, flipH: false, flipV: false };
const MIN_SCALE = 0.05;
const MAX_SCALE = 40;
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const dist = (a: Pt, b: Pt) => Math.hypot(b[0] - a[0], b[1] - a[1]);
/** The angle at B between BA and BC, in degrees. */
const angleAt = (a: Pt, b: Pt, c: Pt) => {
  const v1 = [a[0] - b[0], a[1] - b[1]];
  const v2 = [c[0] - b[0], c[1] - b[1]];
  const dot = v1[0]! * v2[0]! + v1[1]! * v2[1]!;
  const mag = Math.hypot(v1[0]!, v1[1]!) * Math.hypot(v2[0]!, v2[1]!);
  if (mag === 0) return 0;
  return (Math.acos(clamp(dot / mag, -1, 1)) * 180) / Math.PI;
};
const newMeasurementId = () => Math.random().toString(36).slice(2, 10);

export function ImageViewer({
  images,
  index,
  onClose,
  onIndexChange,
  canAnnotate,
}: {
  images: ViewerImage[];
  index: number;
  onClose: () => void;
  onIndexChange?: (index: number) => void;
  /** May save measurements (the imaging permission). Anyone may measure; saving needs this. */
  canAnnotate: boolean;
}) {
  const { t, tx, formatDate } = useI18n();
  const image = images[index];
  const stageRef = useRef<HTMLDivElement>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState<Pt>([0, 0]);
  const [fitted, setFitted] = useState(false);
  const [adj, setAdj] = useState<Adjustments>(DEFAULT_ADJ);
  const [tool, setTool] = useState<Tool>('pan');
  const [measurements, setMeasurements] = useState<ImageMeasurement[]>([]);
  const [calibration, setCalibration] = useState<ImageAnnotations['calibration']>(null);
  const [draft, setDraftState] = useState<Pt[]>([]);
  const draftRef = useRef<Pt[]>([]);
  const setDraft = (next: Pt[]) => {
    draftRef.current = next;
    setDraftState(next);
  };
  const [cursor, setCursor] = useState<Pt | null>(null);
  const [pendingCalibrationPx, setPendingCalibrationPx] = useState<number | null>(null);
  const [knownLength, setKnownLength] = useState('');
  const [pixelSize, setPixelSize] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ x: number; y: number; ox: number; oy: number; moved: boolean; b: number; c: number } | null>(null);

  const kindNames = t.imaging.kinds as Record<string, string>;
  const zoneNames = t.zones.zone as Record<string, string>;

  // A new image: forget the view and read what was saved with it.
  useEffect(() => {
    setNatural(null);
    setFitted(false);
    setDraft([]);
    setPendingCalibrationPx(null);
    setMeasurements([]);
    setCalibration(null);
    setAdj(DEFAULT_ADJ);
    setDirty(false);
    setSaveState('idle');
    setError(null);
    if (!image) return;
    let cancelled = false;
    api
      .imageAnnotations(image.id)
      .then((r) => {
        if (cancelled) return;
        const a = r.annotations ?? {};
        setMeasurements(a.measurements ?? []);
        setCalibration(a.calibration ?? null);
        setAdj({ ...DEFAULT_ADJ, ...(a.adjustments ?? {}) });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [image?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // The stage's size, kept current.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const update = () => setStageSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fit = useCallback(() => {
    if (!natural || !stageSize.w || !stageSize.h) return;
    const rotated = adj.rotate % 180 !== 0;
    const iw = rotated ? natural.h : natural.w;
    const ih = rotated ? natural.w : natural.h;
    setScale(clamp(Math.min(stageSize.w / iw, stageSize.h / ih) * 0.96, MIN_SCALE, MAX_SCALE));
    setOffset([0, 0]);
  }, [natural, stageSize, adj.rotate]);

  useEffect(() => {
    if (natural && stageSize.w && !fitted) {
      fit();
      setFitted(true);
    }
  }, [natural, stageSize, fitted, fit]);

  /** Stage pixels (relative to the stage's centre) → image pixels. */
  const toImage = useCallback(
    (clientX: number, clientY: number): Pt | null => {
      const el = stageRef.current;
      if (!el || !natural) return null;
      const rect = el.getBoundingClientRect();
      let x = (clientX - rect.left - rect.width / 2 - offset[0]) / scale;
      let y = (clientY - rect.top - rect.height / 2 - offset[1]) / scale;
      const r = (-adj.rotate * Math.PI) / 180;
      const rx = x * Math.cos(r) - y * Math.sin(r);
      const ry = x * Math.sin(r) + y * Math.cos(r);
      x = adj.flipH ? -rx : rx;
      y = adj.flipV ? -ry : ry;
      return [x + natural.w / 2, y + natural.h / 2];
    },
    [natural, offset, scale, adj.rotate, adj.flipH, adj.flipV],
  );

  const zoomBy = useCallback(
    (factor: number, aroundClient?: Pt) => {
      const el = stageRef.current;
      const next = clamp(scale * factor, MIN_SCALE, MAX_SCALE);
      const f = next / scale;
      if (el && aroundClient) {
        const rect = el.getBoundingClientRect();
        const px = aroundClient[0] - rect.left - rect.width / 2;
        const py = aroundClient[1] - rect.top - rect.height / 2;
        setOffset([px - (px - offset[0]) * f, py - (py - offset[1]) * f]);
      }
      setScale(next);
    },
    [scale, offset],
  );

  // Wheel zoom must cancel the page scroll, which needs a non-passive listener.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomBy(e.deltaY < 0 ? 1.12 : 1 / 1.12, [e.clientX, e.clientY]);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomBy]);

  const requestClose = useCallback(() => {
    if (dirty && !window.confirm(t.viewer.discardConfirm)) return;
    onClose();
  }, [dirty, onClose, t]);

  const go = useCallback(
    (delta: number) => {
      if (!onIndexChange) return;
      const next = index + delta;
      if (next < 0 || next >= images.length) return;
      if (dirty && !window.confirm(t.viewer.discardConfirm)) return;
      onIndexChange(next);
    },
    [onIndexChange, index, images.length, dirty, t],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')) return;
      if (e.key === 'Escape') {
        if (draft.length) setDraft([]);
        else requestClose();
      } else if (e.key === '+' || e.key === '=') zoomBy(1.25);
      else if (e.key === '-') zoomBy(1 / 1.25);
      else if (e.key === '0') fit();
      else if (e.key === 'r' || e.key === 'R') rotate();
      else if (e.key === 'i' || e.key === 'I') setAdjustment({ invert: !adj.invert });
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  function setAdjustment(patch: Partial<Adjustments>) {
    setAdj((a) => ({ ...a, ...patch }));
    setDirty(true);
    setSaveState('idle');
  }
  function rotate() {
    setAdjustment({ rotate: ((adj.rotate + 90) % 360) as Adjustments['rotate'] });
  }
  function resetView() {
    setAdj(DEFAULT_ADJ);
    setDirty(true);
    setSaveState('idle');
    setFitted(false);
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // A synthetic event has no active pointer to capture; the drag still works.
    }
    drag.current = { x: e.clientX, y: e.clientY, ox: offset[0], oy: offset[1], moved: false, b: adj.brightness, c: adj.contrast };
    setDragging(true);
  }
  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (draft.length) setCursor(toImage(e.clientX, e.clientY));
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!d.moved && Math.abs(dx) + Math.abs(dy) > 4) d.moved = true;
    if (!d.moved) return;
    if (tool === 'window') {
      setAdj((a) => ({ ...a, contrast: clamp(Math.round(d.c + dx * 0.5), -100, 100), brightness: clamp(Math.round(d.b - dy * 0.5), -100, 100) }));
      setDirty(true);
      setSaveState('idle');
    } else {
      setOffset([d.ox + dx, d.oy + dy]);
    }
  }
  function onPointerUp(e: React.PointerEvent<HTMLDivElement>) {
    const d = drag.current;
    drag.current = null;
    setDragging(false);
    if (!d || d.moved) return;
    if (tool !== 'length' && tool !== 'angle' && tool !== 'calibrate') return;
    const p = toImage(e.clientX, e.clientY);
    if (!p) return;
    const next = [...draftRef.current, p];
    const needed = tool === 'angle' ? 3 : 2;
    if (next.length < needed) {
      setDraft(next);
      setCursor(p);
      return;
    }
    setDraft([]);
    setCursor(null);
    if (tool === 'calibrate') {
      setPendingCalibrationPx(dist(next[0]!, next[1]!));
      return;
    }
    setMeasurements((m) => [...m, { id: newMeasurementId(), kind: tool, points: next }]);
    setDirty(true);
    setSaveState('idle');
  }

  function applyKnownLength() {
    const mm = Number(knownLength.replace(',', '.'));
    if (!pendingCalibrationPx || !Number.isFinite(mm) || mm <= 0) return;
    setCalibration({ pxPerMm: Math.round((pendingCalibrationPx / mm) * 1000) / 1000, method: 'known_length' });
    setPendingCalibrationPx(null);
    setKnownLength('');
    setTool('length');
    setDirty(true);
    setSaveState('idle');
  }
  function applyPixelSize() {
    const um = Number(pixelSize.replace(',', '.'));
    if (!Number.isFinite(um) || um <= 0) return;
    setCalibration({ pxPerMm: Math.round((1000 / um) * 1000) / 1000, method: 'pixel_size' });
    setPixelSize('');
    setDirty(true);
    setSaveState('idle');
  }

  async function save() {
    if (!image) return;
    setSaveState('saving');
    setError(null);
    try {
      await api.saveImageAnnotations(image.id, { version: 1, calibration: calibration ?? null, adjustments: adj, measurements });
      setDirty(false);
      setSaveState('saved');
    } catch (e) {
      setSaveState('error');
      setError(describe(e, t));
    }
  }

  const formatLength = (px: number) =>
    calibration ? `${(px / calibration.pxPerMm).toFixed(1)} ${t.viewer.mm}` : `${Math.round(px)} ${t.viewer.px}`;
  const valueOf = (m: ImageMeasurement) =>
    m.kind === 'angle' && m.points.length === 3
      ? `${angleAt(m.points[0]!, m.points[1]!, m.points[2]!).toFixed(1)}°`
      : formatLength(dist(m.points[0]!, m.points[1]!));

  const transform = `translate(${offset[0]}px, ${offset[1]}px) scale(${scale}) rotate(${adj.rotate}deg) scale(${adj.flipH ? -1 : 1}, ${adj.flipV ? -1 : 1})`;
  const filter = `${adj.gamma !== 1 ? 'url(#oxy-viewer-gamma) ' : ''}brightness(${1 + adj.brightness / 100}) contrast(${1 + adj.contrast / 100})${adj.invert ? ' invert(1)' : ''}`;
  // Labels and handles counter the canvas transform so they stay readable at any zoom or rotation.
  const upright = `scale(${adj.flipH ? -1 : 1} ${adj.flipV ? -1 : 1}) rotate(${-adj.rotate}) scale(${1 / scale})`;
  const handle = 5 / scale;

  const hint = useMemo(() => {
    if (pendingCalibrationPx !== null) return t.viewer.knownLengthHint;
    switch (tool) {
      case 'length': return t.viewer.lengthHint;
      case 'angle': return t.viewer.angleHint;
      case 'calibrate': return t.viewer.calibrateHint;
      case 'window': return t.viewer.windowHint;
      default: return t.viewer.panHint;
    }
  }, [tool, pendingCalibrationPx, t]);

  if (!image) return null;

  const subtitle = [
    formatDate(image.at),
    image.toothFdi ? `${t.chart.tooth} ${image.toothFdi}` : null,
    image.zone ? zoneNames[image.zone] ?? image.zone : null,
  ].filter(Boolean).join(' · ');

  const Label = ({ at, text }: { at: Pt; text: string }) => (
    <g transform={`translate(${at[0]} ${at[1]}) ${upright}`} className="viewer-label">
      <rect x={-text.length * 3.6 - 6} y={-19} width={text.length * 7.2 + 12} height={20} rx={5} />
      <text y={-5} textAnchor="middle">{text}</text>
    </g>
  );

  return createPortal(
    <div className="viewer" role="dialog" aria-modal="true" aria-label={t.viewer.title}>
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <filter id="oxy-viewer-gamma">
          <feComponentTransfer>
            <feFuncR type="gamma" amplitude="1" exponent={1 / adj.gamma} offset="0" />
            <feFuncG type="gamma" amplitude="1" exponent={1 / adj.gamma} offset="0" />
            <feFuncB type="gamma" amplitude="1" exponent={1 / adj.gamma} offset="0" />
          </feComponentTransfer>
        </filter>
      </svg>

      <header className="viewer-bar">
        <div className="viewer-title">
          <span>{kindNames[image.kind] ?? image.kind}</span>
          <span className="sub">{subtitle}</span>
          {image.isSynthetic ? <span className="viewer-tag">{t.chart.simulated}</span> : null}
        </div>
        <div className="viewer-nav">
          {images.length > 1 && onIndexChange ? (
            <>
              <button type="button" className="viewer-btn" onClick={() => go(-1)} disabled={index === 0} aria-label={t.viewer.previous}>‹</button>
              <span className="ltr-value">{index + 1} / {images.length}</span>
              <button type="button" className="viewer-btn" onClick={() => go(1)} disabled={index === images.length - 1} aria-label={t.viewer.next}>›</button>
            </>
          ) : null}
          <a className="viewer-btn" href={`/api/imaging/file/${image.id}`} target="_blank" rel="noreferrer noopener">{t.viewer.openOriginal}</a>
          <button type="button" className="viewer-btn" onClick={requestClose}>{t.viewer.close}</button>
        </div>
      </header>

      <div className="viewer-main">
        <nav className="viewer-tools" aria-label={t.viewer.tools}>
          <ToolButton active={tool === 'pan'} label={t.viewer.pan} onClick={() => { setTool('pan'); setDraft([]); }} icon="pan" />
          <ToolButton active={tool === 'window'} label={t.viewer.window} onClick={() => { setTool('window'); setDraft([]); }} icon="window" />
          <ToolButton active={tool === 'length'} label={t.viewer.length} onClick={() => { setTool('length'); setDraft([]); }} icon="length" />
          <ToolButton active={tool === 'angle'} label={t.viewer.angle} onClick={() => { setTool('angle'); setDraft([]); }} icon="angle" />
          <ToolButton active={tool === 'calibrate'} label={t.viewer.calibrate} onClick={() => { setTool('calibrate'); setDraft([]); }} icon="calibrate" />
          <hr />
          <ToolButton label={t.viewer.zoomIn} onClick={() => zoomBy(1.25)} icon="zoomIn" />
          <ToolButton label={t.viewer.zoomOut} onClick={() => zoomBy(1 / 1.25)} icon="zoomOut" />
          <ToolButton label={t.viewer.fit} onClick={fit} icon="fit" />
          <ToolButton label={t.viewer.actual} onClick={() => { setScale(1); setOffset([0, 0]); }} icon="actual" />
          <hr />
          <ToolButton label={t.viewer.rotate} onClick={rotate} icon="rotate" />
          <ToolButton label={t.viewer.flipH} active={adj.flipH} onClick={() => setAdjustment({ flipH: !adj.flipH })} icon="flipH" />
          <ToolButton label={t.viewer.flipV} active={adj.flipV} onClick={() => setAdjustment({ flipV: !adj.flipV })} icon="flipV" />
          <ToolButton label={t.viewer.invert} active={adj.invert} onClick={() => setAdjustment({ invert: !adj.invert })} icon="invert" />
          <hr />
          <ToolButton label={t.viewer.reset} onClick={resetView} icon="reset" />
        </nav>

        <div
          ref={stageRef}
          className={`viewer-stage${dragging ? ' dragging' : ''}`}
          data-tool={tool}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={() => { drag.current = null; setDragging(false); }}
          onDoubleClick={fit}
        >
          {natural ? (
            <div
              className="viewer-canvas"
              style={{ width: natural.w, height: natural.h, marginLeft: -natural.w / 2, marginTop: -natural.h / 2, transform }}
            >
              <img src={`/api/imaging/file/${image.id}`} alt="" style={{ filter }} draggable={false} />
              <svg className="viewer-overlay" width={natural.w} height={natural.h} viewBox={`0 0 ${natural.w} ${natural.h}`}>
                {measurements.map((m) => {
                  const [a, b, c] = m.points;
                  if (!a || !b) return null;
                  const mid: Pt = c ? b : [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
                  return (
                    <g key={m.id} className="viewer-measure">
                      <polyline points={m.points.map((p) => p.join(',')).join(' ')} vectorEffect="non-scaling-stroke" />
                      {m.points.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r={handle} vectorEffect="non-scaling-stroke" />)}
                      <Label at={mid} text={valueOf(m)} />
                    </g>
                  );
                })}
                {draft.length ? (
                  <g className="viewer-measure draft">
                    <polyline points={[...draft, ...(cursor ? [cursor] : [])].map((p) => p.join(',')).join(' ')} vectorEffect="non-scaling-stroke" />
                    {draft.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r={handle} vectorEffect="non-scaling-stroke" />)}
                    {cursor && draft[0] && tool !== 'angle' ? <Label at={[(draft[0][0] + cursor[0]) / 2, (draft[0][1] + cursor[1]) / 2]} text={formatLength(dist(draft[0], cursor))} /> : null}
                  </g>
                ) : null}
              </svg>
            </div>
          ) : null}
          {/* The image itself is loaded once, off-screen, to learn its size. */}
          {!natural ? (
            <img
              src={`/api/imaging/file/${image.id}`}
              alt=""
              style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
              onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
              onError={() => setError(t.common.error)}
            />
          ) : null}
          <div className="viewer-hint">{hint}</div>
        </div>

        <aside className="viewer-side">
          {error ? <div className="viewer-error" role="alert">{error}</div> : null}

          <section>
            <h3>{t.viewer.adjustments}</h3>
            <label className="slider">
              <span>{t.viewer.brightness}</span><span className="ltr-value">{adj.brightness}</span>
              <input type="range" min={-100} max={100} value={adj.brightness} onChange={(e) => setAdjustment({ brightness: Number(e.target.value) })} />
            </label>
            <label className="slider">
              <span>{t.viewer.contrast}</span><span className="ltr-value">{adj.contrast}</span>
              <input type="range" min={-100} max={100} value={adj.contrast} onChange={(e) => setAdjustment({ contrast: Number(e.target.value) })} />
            </label>
            <label className="slider">
              <span>{t.viewer.gamma}</span><span className="ltr-value">{adj.gamma.toFixed(2)}</span>
              <input type="range" min={0.3} max={3} step={0.05} value={adj.gamma} onChange={(e) => setAdjustment({ gamma: Number(e.target.value) })} />
            </label>
          </section>

          <section>
            <h3>{t.viewer.scale}</h3>
            {calibration ? (
              <div className="viewer-row">
                <span className="ltr-value">
                  {tx(t.viewer.calibrated, { value: String(calibration.pxPerMm), method: calibration.method === 'pixel_size' ? t.viewer.methodPixelSize : t.viewer.methodKnownLength })}
                </span>
                <button type="button" className="viewer-btn" onClick={() => { setCalibration(null); setDirty(true); setSaveState('idle'); }}>{t.viewer.clearScale}</button>
              </div>
            ) : (
              <p className="viewer-status">{t.viewer.notCalibrated}</p>
            )}
            {pendingCalibrationPx !== null ? (
              <div className="viewer-stack">
                <label>
                  <span>{t.viewer.knownLength}</span>
                  <input type="text" inputMode="decimal" value={knownLength} onChange={(e) => setKnownLength(e.target.value)} className="ltr-value" autoFocus placeholder="10" />
                </label>
                <div className="viewer-row">
                  <button type="button" className="viewer-btn primary" onClick={applyKnownLength}>{t.viewer.apply}</button>
                  <button type="button" className="viewer-btn" onClick={() => setPendingCalibrationPx(null)}>{t.patients.cancel}</button>
                </div>
              </div>
            ) : (
              <div className="viewer-stack">
                <label>
                  <span>{t.viewer.pixelSize}</span>
                  <div className="viewer-row">
                    <input type="text" inputMode="decimal" value={pixelSize} onChange={(e) => setPixelSize(e.target.value)} className="ltr-value" placeholder="20" />
                    <button type="button" className="viewer-btn" onClick={applyPixelSize} disabled={!pixelSize.trim()}>{t.viewer.apply}</button>
                  </div>
                </label>
                <p className="viewer-status">{t.viewer.pixelSizeHint}</p>
              </div>
            )}
          </section>

          <section>
            <h3>{t.viewer.measurements}</h3>
            {measurements.length === 0 ? (
              <p className="viewer-status">{t.viewer.noMeasurements}</p>
            ) : (
              <ul className="viewer-meas">
                {measurements.map((m, i) => (
                  <li key={m.id}>
                    <span className="viewer-meas-kind">{m.kind === 'angle' ? '∠' : '↔'}</span>
                    <span>{i + 1}. {valueOf(m)}</span>
                    <button type="button" onClick={() => { setMeasurements((list) => list.filter((x) => x.id !== m.id)); setDirty(true); setSaveState('idle'); }} aria-label={t.viewer.remove}>✕</button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            {canAnnotate ? (
              <div className="viewer-row">
                <button type="button" className="viewer-btn primary" onClick={() => void save()} disabled={saveState === 'saving' || !dirty}>
                  {saveState === 'saving' ? t.viewer.saving : t.viewer.save}
                </button>
                <span className="viewer-status">{saveState === 'saved' ? t.viewer.saved : dirty ? t.viewer.unsaved : ''}</span>
              </div>
            ) : (
              <p className="viewer-status">{t.viewer.readOnly}</p>
            )}
          </section>
        </aside>
      </div>
    </div>,
    document.body,
  );
}

const ICONS: Record<string, string> = {
  pan: 'M9 11V6a1.5 1.5 0 0 1 3 0v5m0-3a1.5 1.5 0 0 1 3 0v3m0-1a1.5 1.5 0 0 1 3 0v4a6 6 0 0 1-6 6h-1a6 6 0 0 1-5-2.7L4 14a1.5 1.5 0 0 1 2.5-1.6L9 14',
  window: 'M12 3a9 9 0 1 0 0 18zM12 3a9 9 0 0 1 0 18',
  length: 'M3 17 17 3l4 4L7 21zM8 12l2 2M11 9l2 2M14 6l2 2',
  angle: 'M4 20h16M4 20 16 6M10 20a8 8 0 0 0-2.3-5.7',
  calibrate: 'M3 9h18v6H3zM7 9v3M11 9v3M15 9v3M19 9v3',
  zoomIn: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM21 21l-5-5M11 8v6M8 11h6',
  zoomOut: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM21 21l-5-5M8 11h6',
  fit: 'M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5',
  actual: 'M4 4h16v16H4zM9 15V9l-1.5 1M14 9h2v6',
  rotate: 'M20 12a8 8 0 1 1-2.3-5.7M20 4v4h-4',
  flipH: 'M12 3v18M8 7 3 12l5 5zM16 7l5 5-5 5z',
  flipV: 'M3 12h18M7 8l5-5 5 5zM7 16l5 5 5-5z',
  invert: 'M12 3a9 9 0 1 0 0 18zM12 3v18',
  reset: 'M4 12a8 8 0 1 0 2.3-5.7M4 4v4h4',
};

function ToolButton({ label, icon, onClick, active = false }: { label: string; icon: string; onClick: () => void; active?: boolean }) {
  return (
    <button type="button" className="viewer-tool" onClick={onClick} aria-pressed={active} title={label} aria-label={label}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={ICONS[icon] ?? ''} />
      </svg>
    </button>
  );
}
